import { useEffect, useLayoutEffect, useRef } from "react";

export type HotkeyMap = Record<string, (e: KeyboardEvent) => void>;
export type HotkeyOptions = {
  enabled?: boolean;
  /** Combos que também valem com o foco num campo editável (ex.: "alt+s", "escape" no FormPage). */
  allowInInputs?: string[];
  /** Roda com um <dialog open> na página, desde que o alvo esteja dentro dele. */
  insideModal?: boolean;
};

export function isEditableTarget(t: EventTarget | null): boolean {
  return t instanceof HTMLElement && (t.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName));
}

/**
 * Normaliza a tecla: "alt+s", "ctrl+shift+k", "escape", "/".
 * Com alt/ctrl/meta a letra vem de e.code (KeyS → s): no macOS Option+S gera e.key "ß" (FE8).
 * Sem esses modificadores vale e.key, sem shift (em vários layouts "/" exige shift).
 */
export function comboOf(e: KeyboardEvent): string {
  const mods = [e.altKey && "alt", e.ctrlKey && "ctrl", e.metaKey && "meta"].filter((m): m is string => !!m);
  if (mods.length === 0) return e.key.toLowerCase();
  const code = e.code ?? "";
  const key = /^Key[A-Z]$/.test(code)
    ? code.slice(3).toLowerCase()
    : /^Digit\d$/.test(code)
      ? code.slice(5)
      : e.key.toLowerCase();
  return [...mods, ...(e.shiftKey ? ["shift"] : []), key].join("+");
}

export function useHotkeys(map: HotkeyMap, opts: HotkeyOptions = {}) {
  const latest = useRef({ map, opts });
  useLayoutEffect(() => {
    latest.current = { map, opts };
  });
  const enabled = opts.enabled ?? true;
  useEffect(() => {
    if (!enabled) return;
    const onKey = (e: KeyboardEvent) => {
      const { map: m, opts: o } = latest.current;
      const combo = comboOf(e);
      const fn = m[combo];
      if (!fn) return;
      // Antes do preventDefault: digitar "/" em Website ou numa data não pode ser engolido (FE3).
      if (isEditableTarget(e.target) && !o.allowInInputs?.includes(combo)) return;
      // Só diálogos abertos contam; o <dialog> fechado continua montado na página (FE6).
      const modal = document.querySelector("dialog[open]");
      if (modal && !(o.insideModal && e.target instanceof Node && modal.contains(e.target))) return;
      e.preventDefault();
      fn(e);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [enabled]);
}
